import { useQuery } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Lock, RefreshCw, Copy, LogOut, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import useAuthStore from '@/stores/authStore';

export interface GateData {
  blocked: boolean;
  expired?: boolean;
  plan?: string | null;
  plan_version?: number;
  is_admin?: boolean;
  portal_url?: string;
  /** Nur Bestandskunden (zentrale Onboarding-Zugangsdaten) — bei Neukunden immer null */
  onboarding?: { email: string; temp_password: string; portal_url?: string } | null;
}

export const GATE_QUERY_KEY = ['license-gate'];
export const fetchGate = async (force = false) =>
  (await api.get('/license/gate', { params: force ? { force: 1 } : undefined })).data?.data as GateData;

/**
 * Tam ekran "Lizenz abgelaufen" sayfası (/license-expired). Overlay DEĞİL: ayrı route,
 * AppShell hiç render edilmez. Sunucu tarafı kapı (402 LICENSE_EXPIRED) asıl güvenliği sağlar;
 * bu sayfa yalnız kullanıcıya ne yapacağını söyler. Lisans tekrar geçerliyse dashboard'a döner.
 */
export default function LicenseExpired() {
  const { t } = useTranslation();
  const logout = useAuthStore((s) => s.logout);

  const q = useQuery({ queryKey: GATE_QUERY_KEY, queryFn: () => fetchGate(), refetchInterval: 60_000, retry: false });
  const data = q.data;

  if (q.isLoading) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-950"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }
  if (data && !data.blocked) return <Navigate to="/dashboard" replace />;

  const portal = data?.onboarding?.portal_url || data?.portal_url || 'https://app.vyndesk.com/license';

  const recheck = async () => {
    try {
      const r = await fetchGate(true);
      if (!r?.blocked) {
        toast.success(t('settings:licenseGate.nowActive', 'Lizenz ist aktiv — willkommen zurück!'));
        window.location.assign('/dashboard');
      } else {
        toast.info(t('settings:licenseGate.stillBlocked', 'Noch keine aktive Lizenz gefunden. Nach der Zahlung kann es bis zu einer Minute dauern.'));
      }
    } catch {
      toast.error(t('errors:generic', 'Etwas ist schiefgelaufen'));
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 p-4">
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-white p-6 shadow-2xl dark:bg-[#1b1d20]">
        <div className="mb-4 flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-50 text-red-600 dark:bg-red-500/10">
            <Lock className="h-5 w-5" />
          </span>
          <div>
            <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{t('settings:licenseGate.title', 'Lizenz abgelaufen')}</h1>
            <p className="text-xs text-slate-500">{t('settings:licenseGate.subtitle', 'Der Zugriff auf VynDesk ist pausiert, bis ein Paket gewählt und bezahlt wurde.')}</p>
          </div>
        </div>

        {data?.is_admin || data?.onboarding ? (
          <>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {t('settings:licenseGate.adminText', 'Wählen Sie im VynDesk Lizenz-Portal ein Paket. Nach der Zahlung wird dieser Desk automatisch wieder freigeschaltet — ohne Neuinstallation, alle Daten bleiben erhalten.')}
            </p>
            {data?.onboarding && (
              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 dark:border-amber-500/30 dark:bg-amber-500/10">
                <p className="mb-2 text-xs font-semibold text-amber-900 dark:text-amber-200">{t('settings:licenseGate.credentialsTitle', 'Ihre Zugangsdaten für das Lizenz-Portal')}</p>
                <div className="space-y-1.5 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-slate-500">{t('settings:licenseGate.email', 'E-Mail')}</span>
                    <span className="truncate font-medium text-slate-900 dark:text-slate-100">{data.onboarding.email}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-slate-500">{t('settings:licenseGate.password', 'Passwort')}</span>
                    <span className="font-mono font-medium text-slate-900 dark:text-slate-100">{data.onboarding.temp_password}</span>
                  </div>
                </div>
                <button type="button"
                  onClick={() => { navigator.clipboard.writeText(`${data.onboarding!.email}\n${data.onboarding!.temp_password}`); toast.success(t('settings:licenseGate.copied', 'Kopiert')); }}
                  className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-amber-800 hover:underline dark:text-amber-200">
                  <Copy className="h-3 w-3" /> {t('settings:licenseGate.copy', 'Zugangsdaten kopieren')}
                </button>
                <p className="mt-2 text-[11px] text-amber-800/80 dark:text-amber-200/70">{t('settings:licenseGate.credentialsHint', 'Bitte ändern Sie das Passwort nach dem ersten Login im Portal.')}</p>
              </div>
            )}
            <div className="mt-5 flex flex-wrap gap-2">
              <Button asChild>
                <a href={portal} target="_blank" rel="noreferrer"><ExternalLink className="mr-2 h-4 w-4" />{t('settings:licenseGate.openPortal', 'Lizenz-Portal öffnen')}</a>
              </Button>
              <Button variant="outline" onClick={recheck}><RefreshCw className="mr-2 h-4 w-4" />{t('settings:licenseGate.recheck', 'Ich habe bezahlt — erneut prüfen')}</Button>
            </div>
          </>
        ) : (
          <p className="text-sm text-slate-600 dark:text-slate-300">
            {t('settings:licenseGate.userText', 'Bitte wenden Sie sich an Ihren Administrator — die Lizenz muss im VynDesk Lizenz-Portal verlängert werden.')}
          </p>
        )}

        <div className="mt-4 border-t border-slate-100 pt-3 dark:border-white/10">
          <button type="button" onClick={() => logout()} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">
            <LogOut className="h-3 w-3" /> {t('settings:licenseGate.logout', 'Abmelden')}
          </button>
        </div>
      </div>
    </div>
  );
}
